import moment from "moment";

// A work order by number, or "their request" while it's still a request.
const woRef = (meta) => (meta.woNumber ? `WO# ${meta.woNumber}` : "their request");

// Per-type display contract, mirroring the technician app's Notifications
// screen (backend counterpart: user-notification.types.ts). Types this build
// doesn't know fall back to server-provided meta.title/meta.body text and are
// hidden entirely when that's absent — a newer server must never get its events
// rendered with another type's copy.
const NOTIFICATION_RENDERERS = {
  WO_ASSIGNED: {
    label: "NEW WO ASSIGNED",
    filterLabel: "New WO Assigned",
    title: (meta) => [
      { text: `WO# ${meta.woNumber ?? meta.woId ?? ""}`, bold: true },
      { text: " was assigned to you" },
    ],
    detail: (meta) => meta.customerName,
  },
  TICKET_MESSAGE: {
    label: "NEW MESSAGE",
    filterLabel: "New Message",
    title: (meta) => [
      { text: meta.actorName || "Someone", bold: true },
      { text: ` replied on Ticket #${meta.ticketNumber ?? meta.ticketId ?? ""}` },
    ],
    detail: (meta) => meta.ticketSubject,
  },
  WO_DUE_TODAY: {
    label: "DUE TODAY",
    filterLabel: "Due Today",
    title: (meta) => [
      { text: `WO# ${meta.woNumber ?? meta.woId ?? ""}`, bold: true },
      {
        text: meta.scheduledTime ? ` is scheduled today at ${meta.scheduledTime}` : " is scheduled today",
      },
    ],
    detail: (meta) => meta.customerName,
  },
  TECH_NOTE_ADDED: {
    label: "NEW TECH NOTE",
    filterLabel: "New Tech Note",
    title: (meta) => [
      { text: meta.actorName || "Someone", bold: true },
      { text: ` added a new Tech Note to WO# ${meta.woNumber ?? meta.woId ?? ""}` },
    ],
    detail: (meta) => meta.customerName,
  },
  // Customer activity from the customer apps (item 422). The actor is the
  // customer app user; a request has no WO number yet.
  CUSTOMER_REQUEST: {
    label: "NEW REQUEST",
    filterLabel: "Customer Request",
    title: (meta) => [
      { text: meta.actorName || "A customer", bold: true },
      { text: ` requested ${meta.isQuote ? "a quote" : "service"}` },
      ...(meta.fromRecommendationWoNumber
        ? [{ text: ` from a recommendation on WO# ${meta.fromRecommendationWoNumber}` }]
        : []),
    ],
    detail: (meta) => meta.customerName,
  },
  CUSTOMER_MESSAGE: {
    label: "CUSTOMER CHAT",
    filterLabel: "Customer Chat",
    title: (meta) => [
      { text: meta.actorName || "A customer", bold: true },
      { text: ` sent a message on ${woRef(meta)}` },
    ],
    detail: (meta) => (meta.messagePreview ? `“${meta.messagePreview}”` : meta.customerName),
  },
  PROPOSAL_RESPONSE: {
    label: "PROPOSAL",
    filterLabel: "Proposal Signed / Revision",
    title: (meta) => [
      { text: meta.actorName || "A customer", bold: true },
      {
        text:
          meta.response === "signed"
            ? ` signed the proposal on ${woRef(meta)}`
            : ` asked for a revision on ${woRef(meta)}`,
      },
    ],
    detail: (meta) => (meta.response === "revision" && meta.note ? `“${meta.note}”` : meta.customerName),
  },
  RECOMMENDATION_RESPONSE: {
    label: "RECOMMENDATION",
    filterLabel: "Recommendation Response",
    title: (meta) => [
      { text: meta.actorName || "A customer", bold: true },
      { text: ` marked a recommendation Not Now on ${woRef(meta)}` },
    ],
    detail: (meta) => meta.recommendationText || meta.customerName,
  },
};

const FALLBACK_RENDERER = {
  label: "NOTIFICATION",
  title: (meta) => (meta.title ? [{ text: meta.title, bold: true }] : null),
  detail: (meta) => meta.body,
};

export const getRenderer = (item) => NOTIFICATION_RENDERERS[item.type] || FALLBACK_RENDERER;

// Options for the notifications page's type filter. Built from the renderer map
// so a type this build can't render never appears as something to filter by.
export const NOTIFICATION_TYPE_OPTIONS = Object.entries(NOTIFICATION_RENDERERS).map(([value, renderer]) => ({
  value,
  label: renderer.filterLabel,
}));

// Where a row leads when clicked: ticket rows open the thread, everything else
// the work order. Tech note rows land on the note itself — the WO page opens the
// Tech Notes card and highlights this id (see TechNotes.jsx). Null means the row
// has nothing to open.
export const getNotificationPath = (item) => {
  const meta = item.meta || {};

  const ticketId = meta.ticketId ?? (item.targetType === "ticket" ? item.targetId : null);
  if (ticketId) return `/ticket/detail/${ticketId}`;

  const woId = meta.woId ?? (item.targetType === "wo" ? item.targetId : null);
  if (!woId) return null;
  return meta.techNoteId ? `/work/${woId}?techNoteId=${meta.techNoteId}` : `/work/${woId}`;
};

// A row is renderable when its type is known or the server sent generic text.
export const isRenderable = (item) => Boolean(getRenderer(item).title(item.meta || {}));

// Compact relative time per the design ("2 min ago", "Yesterday", "2 days
// ago") — moment's fromNow() is too verbose for the list rows.
export const formatRelativeTime = (value) => {
  const then = moment(value);
  const now = moment();
  const mins = now.diff(then, "minutes");
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  if (now.isSame(then, "day")) {
    const hours = now.diff(then, "hours");
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  const days = now.clone().startOf("day").diff(then.clone().startOf("day"), "days");
  if (days <= 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return then.format("MM/DD/YYYY");
};
